import React, { FC, useCallback, useEffect, useState } from 'react';
import classnames from 'classnames';
import { Link } from 'react-router-dom';
import Svg from 'svg-inline-react';
import { debounce } from 'lodash';
import { OutsideClick } from '@src/components/OutsideClick';
import FrAddSvg from '@src/svgs/fr-add.svg';
import FrSearchSvg from '@src/svgs/fr-search.svg';
import { NEW_FUNNEL_ROUTE } from '@src/Routes/links';
import { OpeningSearchTagNameEnum, useGetOpeningNamesQuery } from '@src/graphql/generated';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { trackEvent } from '@src/metrics';
import { OpeningsPopupProps } from './props';
import './styles.scss';

export const OpeningsPopup: FC<OpeningsPopupProps> = ({
  openingsPopupCoordinates,
  setOpeningsPopupCoordinates,
  onOpeningSelect,
  onClose,
}) => {
  const isOpen = !!openingsPopupCoordinates;

  const [searchPattern, setSearchPattern] = useState<string>('');

  const {
    data,
    loading: isLoading,
    refetch,
  } = useGetOpeningNamesQuery({
    variables: {
      paginationOptions: {
        limit: 10,
      },
      searchTags: [{ tag: OpeningSearchTagNameEnum.STATE, value: 'active' }],
    },
  });

  const openings = data?.openings.result || [];

  const onSearch = useCallback(
    debounce((searchValue: string) => {
      refetch({
        searchPatterns: [{ pattern: searchValue, tag: OpeningSearchTagNameEnum.TITLE }],
      });
    }, 350),
    [],
  );

  const onChange = (value: string) => {
    setSearchPattern(value);
    onSearch(value);
  };

  useEffect(() => {
    if (isOpen) {
      trackEvent({
        context: MetricContext.SOURCE,
        interaction: MetricAction.CONNECT_POPUP_OPENED,
      });
    }
  }, [isOpen]);

  return (
    <OutsideClick
      addOutsideClickListener={isOpen}
      onOutsideClick={() => {
        setOpeningsPopupCoordinates?.(undefined);
        onClose?.();
      }}
    >
      <div className={classnames('ta-section-footer-popup')}>
        {isLoading ? <h4>Loading...</h4> : null}

        <div className="ta-section-footer-popup__title">
          {!isLoading && openings.length ? 'Select a position' : null}
          {!isLoading && !openings.length
            ? 'There are currently no active positions associated with your company.'
            : null}
        </div>

        {openings.length ? (
          <>
            <div className="ta-section-footer-popup__search-wrapper">
              <Svg className="ta-section-footer-popup__search-icon" src={FrSearchSvg} />

              <input
                className="ta-section-footer-popup__search-field"
                onChange={event => onChange(event.target.value)}
                placeholder="Search...."
                type="text"
                value={searchPattern}
              />
            </div>

            <div className="ta-section-footer-popup__openings">
              {openings.map(opening => {
                return (
                  <div
                    className="ta-section-footer-popup__opening"
                    key={opening.id}
                    onClick={event => {
                      event.stopPropagation();
                      onOpeningSelect(opening.id, opening.title, opening.pitch);
                    }}
                  >
                    <div className="ta-section-footer-popup__opening-title">{opening.title}</div>

                    <div className="ta-section-footer-popup__opening-button">Select</div>
                  </div>
                );
              })}
            </div>
          </>
        ) : null}

        <div className="ta-section-footer-popup__footer">
          <Link
            className="ta-section-footer-popup__add-opening-button"
            to={NEW_FUNNEL_ROUTE}
            onClick={() => {
              setOpeningsPopupCoordinates?.(undefined);
              onClose?.();
            }}
          >
            <Svg className="ta-section-footer-popup__add-icon" src={FrAddSvg} />
            Create a position
          </Link>
        </div>
      </div>
    </OutsideClick>
  );
};
